const EMPTY_CONTEXT = {
  content_type: 'none',
  video_id: null,
  root_id: null,
  virtual_id: null,
  duration: Infinity,
  start_time: 0,
  channel_id: null
};
const changeConsumers = new Set();
const resetConsumers = new Set();

let console = {
  ...window.console,
  log: () => void 0,
  group: () => void 0,
  groupEnd: () => void 0
};

if (process.env.NODE_ENV === 'development') {
  console = window.console;
}

/**
 * Interactive context, should describe object on top off which interactive is started.
 * @param pPlayer
 * @constructor
 */
export function Context(pPlayer) {
  let isConsumable = false;
  const _ctx = {
    ...EMPTY_CONTEXT
  };

  pPlayer.on('interactive:stop', () => {
    resetContext();
  });

  // eslint-disable-next-line complexity
  pPlayer.on("epg:channelSwitch", (ev) => {
    // a bit strange case, when user goes to the same channel, but ok
    // details: https://jira.x2sy.com/browse/RMI-2087
    const { isCurrent, id: nextChannelId } = ev.data;
    const videoInfo = pPlayer.get("videoInfo");
    const currentChannelId = videoInfo?.channel_id || videoInfo?.video_id;

    if (isCurrent && nextChannelId === currentChannelId) {
      return;
    }

    console.log("Switch channel, reset context", ev);
    resetContext();
  });

  let defferCall;

  this.onChange = function(cb) {
    changeConsumers.add(cb);
    return () => {
      changeConsumers.delete(cb);
    };
  };


  this.onReset = function(cb) {
    resetConsumers.add(cb);
    return () => {
      resetConsumers.delete(cb);
    };
  };

  this.get = function() {
    if (isConsumable) {
      return Promise.resolve({ ..._ctx });
    }
    return new Promise((resolve) => {
      const removeSingleCb = this.onChange((ctx) => {
        removeSingleCb();
        resolve(ctx);
      });
    });
  };

  pPlayer.on('property:videoInfo', onNewVideoInfo);

  let handledKey;
  pPlayer.on('property:interactiveChannel:currentPlayingProgram',
    // eslint-disable-next-line complexity
    function onChangeCurrentPlayingProgram(_event) {
    console.log('got new current program from DVR', _event);

    if (_event.data === null) {
      console.log('skip handling due to no program, rely on handling new videoInfo');
      return;
    }

    if (_event.data.channel?.id !== _ctx.channel_id) {
      console.log('skip handling, due to channel mismatch, rely on changing video info');
      return;
    }

    const suggestUniqKey = getVirtualId(_event.data);

    if (suggestUniqKey === handledKey) {
      /* this one must handle two cases:
      * - seek on DVR, e.g. having the same virtualObjectId
      * - normal switch between object on vodChannel where virtualObjectId is empty string
      */
      console.log('skip handling, virtual id is the same');
      return;
    }

    handledKey = suggestUniqKey;


    const videoInfo = pPlayer.get('videoInfo');

    if (!videoInfo?.video_id) {
      console.log('skip handling, no videoInfo');
      return;
    }

    onNewVideoInfo({name: 'fake-event', data: videoInfo});
  });

  // private methods

  function getDurationVOD() {
    return getProp('duration', _ => _ !== 0)
      .then((duration) => ({duration: parseInt(duration)}));
  }

  async function getProgramData(_videoInfo) {
    const currentChannel = await getProp('epg:currentChannel', Boolean);
    const data = {
      virtual_id: null,
      start_time: 0,
      duration: Infinity,
      channel_id: currentChannel.id
    };

    if (currentChannel.isProgramAllocated) {
      return Promise.resolve(Object.assign(data, getData()));
    } else {
      return new Promise((resolve) => {
        const event = 'epg:currentProgramListUpdated';
        const listener = (ev) => {
          if (ev.data.id !== currentChannel.id){
            return;
          }
          pPlayer.off(event, listener);
          setTimeout(() => {
            resolve(Object.assign(data, getData()));
          }/**/);
        };

        pPlayer.on(event, listener);
      });
    }

    function getData(){
      const program = pPlayer.get('interactiveChannel:currentPlayingProgram') ?? pPlayer.get('epg:currentProgram');
      if (!program){
        return;
      }
      return {
        virtual_id: getVirtualId(program),
        start_time: program.startTimestamp,
        duration: getDuration(program),
      };
    }

  }

  // eslint-disable-next-line complexity
  function onNewVideoInfo(pEvent) {
    const videoInfo = pEvent.data;

    if (videoInfo.content_type === 'm_ch') {
      console.log('completely ignore initial, fake start');
      return;
    }

    console.group('RMI: start collect context');
    console.log(pEvent);

    const nextContext = {
      content_type: videoInfo.content_type,
      video_id: videoInfo.video_id,
      // channel_id: videoInfo.video_id,
      root_id: videoInfo.root_video_id,
      virtual_id: videoInfo.virtual_id,
      duration: videoInfo.duration,
      start_time: videoInfo.startTimestamp
    };

    const thisIsTVIndeed = videoInfo.is_tv || ['m_ch_vod', 'tv_dvr_vod', 'tv_linear_vod'].includes(videoInfo.content_type);

    let dataCollector = thisIsTVIndeed ? getProgramData : getDurationVOD;

    if (videoInfo.is_live && !videoInfo.is_tv) {

      const {
        live_start: start_time = 0,
        live_stop: stop_time = Infinity
      } = videoInfo;
      const duration = stop_time - start_time;

      dataCollector = () => {
        return Promise.resolve({
          start_time,
          duration
        });
      };
    }

    dataCollector(videoInfo)
      // eslint-disable-next-line complexity
      .then((data) => {
        nextContext.virtual_id ??= data.virtual_id;
        nextContext.start_time ??= data.start_time;
        nextContext.duration ??= data.duration;
        nextContext.channel_id ??= data.channel_id;

        console.log('RMI: new context', nextContext);
        console.groupEnd();

        return nextContext;
      })
      .then(setContext);
  }

  async function getProp(propName, validate = _ => true) {
    const value = pPlayer.get(propName);
    if (validate(value)) {
      return Promise.resolve(value);
    }
    return new Promise((resolve) => {
      pPlayer.on(`property:${propName}`, function onceListener(event) {
        pPlayer.off(`property:${propName}`, onceListener);
        resolve(event.data);
      });
    });
  }

  function resetContext() {
    console.log('Reset context');
    isConsumable = false;
    _ctx.content_type = 'none';
    _ctx.video_id = null;
    _ctx.root_id = null;
    _ctx.virtual_id = null;
    _ctx.duration = Infinity;
    _ctx.start_time = 0;
    _ctx.channel_id = null;
    resetConsumers.forEach((cb) => cb());
  }

  function setContext(newCtx) {
    console.log('Received new context', { ...newCtx });

    const next = {
      ...EMPTY_CONTEXT,
      ...newCtx
    };

    const hasChanges = Object.keys(next).some((key) => next[key] !== _ctx[key]);
    if (!hasChanges) {
      console.log('No changes for the context, skip update');
      return;
    }
    Object.assign(_ctx, next);
    isConsumable = true;

    clearTimeout(defferCall);
    defferCall = setTimeout(() => {
      changeConsumers.forEach((cb) => cb({ ..._ctx }));
      console.log('Send new context to consumers', {..._ctx});
    });
  }
}

/**
 * The `...Id` from site and `...ID` from STV
 * @param program
 * @return {string}
 */
function getVirtualId(program = {}) {
  return program.virtualObjectId ?? program.virtualObjectID;
}

/**
 * Yet again, we gave `duration` at the site context and does not at STV
 * @param program
 * @return {number|null}
 */
function getDuration(program) {
  if (program.duration){
    return program.duration;
  }
  let duration = program.endTimestamp - program.startTimestamp;
  if (Number.isFinite(duration)){
    return duration;
  }
  return null;
}
